import { useAuthStore } from '@/stores/authStore'

const BASE_URL = import.meta.env.VITE_API_URL || ''

// Endpoints that should NOT trigger 401 refresh-retry logic
const AUTH_ENDPOINTS = ['/api/v1/auth/request-otp', '/api/v1/auth/verify-otp', '/api/v1/auth/refresh', '/api/v1/auth/logout']

export class ApiError extends Error {
  constructor(
    message: string,
    public code?: string,
    public status?: number
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

// Mutex: single in-flight refresh promise shared across concurrent 401s
let refreshPromise: Promise<boolean> | null = null

class ApiClient {
  private baseUrl: string

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl
  }

  private isAuthEndpoint(endpoint: string): boolean {
    return AUTH_ENDPOINTS.some((authPath) => endpoint.startsWith(authPath))
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {}
  ): Promise<T> {
    const token = useAuthStore.getState().token

    const headers: HeadersInit = {
      'Content-Type': 'application/json',
      ...(token && { Authorization: `Bearer ${token}` }),
      ...options.headers,
    }

    const response = await fetch(`${this.baseUrl}${endpoint}`, {
      ...options,
      headers,
    })

    // Handle 401 — attempt refresh-then-retry for non-auth endpoints
    if (response.status === 401) {
      if (this.isAuthEndpoint(endpoint)) {
        // Auth endpoints: don't retry, just throw
        useAuthStore.getState().logout()
        throw new ApiError('Session expired', 'UNAUTHORIZED', 401)
      }

      // Use mutex so concurrent 401s share a single refresh call
      if (!refreshPromise) {
        refreshPromise = useAuthStore.getState().refreshAccessToken().finally(() => {
          refreshPromise = null
        })
      }

      const refreshed = await refreshPromise

      if (refreshed) {
        // Retry with the fresh access token
        const newToken = useAuthStore.getState().token
        const retryHeaders: HeadersInit = {
          'Content-Type': 'application/json',
          ...(newToken && { Authorization: `Bearer ${newToken}` }),
          ...options.headers,
        }

        const retryResponse = await fetch(`${this.baseUrl}${endpoint}`, {
          ...options,
          headers: retryHeaders,
        })

        if (retryResponse.ok) {
          const contentType = retryResponse.headers.get('content-type')
          if (contentType?.includes('application/json')) {
            return retryResponse.json()
          }
          return {} as T
        }

        // Retry also failed — give up
        useAuthStore.getState().logout()
        throw new ApiError('Session expired', 'UNAUTHORIZED', retryResponse.status)
      }

      // Refresh failed — already logged out inside refreshAccessToken
      throw new ApiError('Session expired', 'UNAUTHORIZED', 401)
    }

    if (!response.ok) {
      let errorMessage = 'An error occurred'
      let errorCode: string | undefined

      try {
        const error = await response.json()
        errorMessage = error.error || error.detail || errorMessage
        errorCode = error.code
      } catch {
        // Response might not be JSON
      }

      throw new ApiError(errorMessage, errorCode, response.status)
    }

    // Handle empty responses
    const contentType = response.headers.get('content-type')
    if (contentType?.includes('application/json')) {
      return response.json()
    }

    return {} as T
  }

  async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' })
  }

  async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    })
  }

  async put<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(data),
    })
  }

  async patch<T>(endpoint: string, data: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(data),
    })
  }

  async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' })
  }
}

export const api = new ApiClient(BASE_URL)
